import { BlocksConfigPropsType } from "@pathwright/blocks-engine"
import { BlocksOpenSupportRequest } from "@pathwright/blocks-engine/src/types"
import BlockContentContainer from "@pathwright/blocks/src/main/BlockContentContainer"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import { Suspense } from "react"
import { useIntercomContext } from "../intercom/IntercomProvider"
import { useWebBlocksClient } from "./client"
import { usePathwrightContext } from "../pathwright/PathwrightContext"

type BlockContentProps = BlocksConfigPropsType & {
  showLoading?: boolean
}

// Provides a stable handler to open a support request via Intercom or mailto fallback
const useOpenSupportRequest = (): BlocksOpenSupportRequest => {
  const context = usePathwrightContext()
  const { showIntercomUIWithMessage, isIntercomAvailable } =
    useIntercomContext()

  return ({ message }: { error: string; message: string }): void => {
    if (isIntercomAvailable && showIntercomUIWithMessage) {
      try {
        showIntercomUIWithMessage(message)
        return
      } catch {
        console.error("Error showing Intercom support request", message)
      }
    } else {
      const to = "hello@pathwright.com"
      const subject = `Block error in ${context.school.name}`
      const mailto = `mailto:${to}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(message)}`
      window.open(mailto)
    }
  }
}

const BlockContent = ({ showLoading = true, ...props }: BlockContentProps) => {
  const context = usePathwrightContext()
  const openSupportRequest = useOpenSupportRequest()
  const firebaseConfig = context?.integrations?.firebase
  const firebaseAuthToken = firebaseConfig?.auth_token
  const graphQLClient = useWebBlocksClient(props.accountID || context.school.id)

  return (
    <Suspense fallback={showLoading ? <LoadingCircle /> : null}>
      <BlockContentContainer
        {...props}
        graphQLClient={graphQLClient}
        isSuperuser={!!context.me?.permissions?.has_pathwright_staff_access}
        firebaseConfig={{ ...(firebaseConfig || {}), firebaseAuthToken }}
        accountID={props.accountID || context.school.id}
        openSupportRequest={openSupportRequest}
      />
    </Suspense>
  )
}

export default BlockContent
